import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Shell = styled.section`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 1rem 0 1.25rem;
  perspective: 1400px;

  @media (max-width: 760px) {
    padding: .35rem 0 .85rem;
  }
`;

const Stage = styled.div`
  position: relative;
  width: 100%;
  height: clamp(540px, 62vw, 680px);
  display: grid;
  place-items: center;
  transform-style: preserve-3d;

  @media (max-width: 760px) {
    height: clamp(390px, 108vw, 500px);
  }
`;

const Slot = styled(motion.div)`
  position: absolute;
  width: clamp(320px, 60vw, 480px);
  height: clamp(520px, 58vw, 640px);
  will-change: transform, filter, opacity;
  transform-style: preserve-3d;

  @media (max-width: 760px) {
    width: min(74vw, 288px);
    height: clamp(360px, 95vw, 440px);
  }
`;

const Nav = styled.div`
  display: flex; gap: .5rem; justify-content: center; align-items: center;
  margin-top: 3rem;

  @media (max-width: 760px) {
    margin-top: 1rem;
    gap: .35rem;
  }
`;

const Arrow = styled.button`
  border: 1px solid rgba(255,255,255,.14);
  background: rgba(255,255,255,.06);
  color: #E8EAED;
  border-radius: 999px;
  padding: .55rem .8rem;
  font-weight: 900;
  cursor: pointer;
  &:hover { background: rgba(255,255,255,.12); }

  @media (max-width: 760px) {
    padding: .42rem .62rem;
    font-size: .85rem;
  }
`;

const Dots = styled.div`
  display: flex; gap: .4rem; margin-left: .25rem;
`;

const Dot = styled.button<{ $active?: boolean }>`
  width: 8px; height: 8px; border-radius: 50%;
  border: 0;
  background: ${({ $active }) => ($active ? '#E8EAED' : 'rgba(255,255,255,.35)')};
  transform: ${({ $active }) => ($active ? 'scale(1.25)' : 'none')};
  cursor: pointer;
`;

type Props = {
    children: React.ReactNode;
    initialIndex?: number;
    onChange?: (i: number) => void;
};

export default function Carousel3D({ children, initialIndex = 1, onChange }: Props) {
    const items = useMemo(() => React.Children.toArray(children), [children]);
    const [idx, setIdx] = useState(
        Math.min(Math.max(0, initialIndex), Math.max(0, items.length - 1))
    );

    useEffect(() => {
        setIdx(Math.min(Math.max(0, initialIndex), Math.max(0, items.length - 1)));
    }, [initialIndex, items.length]);

    const prev = () => {
        const n = (idx - 1 + items.length) % items.length;
        setIdx(n); onChange?.(n);
    };
    const next = () => {
        const n = (idx + 1) % items.length;
        setIdx(n); onChange?.(n);
    };

    // Precompute left/center/right for 3 items (works fine for >3 too)
    const pos = (i: number) => {
        const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches;
        const sideOffset = isMobile ? 188 : 360;
        const farOffset = isMobile ? 86 : 120;
        const centerZ = isMobile ? 110 : 160;
        const sideZ = isMobile ? 18 : 40;
        const leftRightRotation = isMobile ? 14 : 24;
        const sideScale = isMobile ? 0.88 : 0.92;
        const farScale = isMobile ? 0.72 : 0.8;
        if (items.length === 1) return { x: 0, z: 120, rot: 0, sc: 1, o: 1, blur: 0, zIndex: 30 };
        const left = (idx - 1 + items.length) % items.length;
        const right = (idx + 1) % items.length;

        if (i === idx) return { x: 0, z: centerZ, rot: 0, sc: 1.0, o: 1.0, blur: 0, zIndex: 50 };
        if (i === left) return { x: -sideOffset, z: sideZ, rot: leftRightRotation, sc: sideScale, o: 0.9, blur: 0.2, zIndex: 40 };
        if (i === right) return { x: sideOffset, z: sideZ, rot: -leftRightRotation, sc: sideScale, o: 0.9, blur: 0.2, zIndex: 40 };
        // farther items fall back
        const dist = Math.min((Math.abs(i - idx)), 2);
        const dir = i < idx ? -1 : 1;
        return { x: dir * (sideOffset + (dist - 1) * farOffset), z: -80, rot: dir * (isMobile ? -18 : -32), sc: farScale, o: 0.6, blur: 0.6, zIndex: 20 };
    };

    return (
        <Shell aria-roledescription="carousel" aria-label="Plans carousel">
            <Stage>
                {items.map((child, i) => {
                    const { x, z, rot, sc, o, blur, zIndex } = pos(i);
                    return (
                        <Slot
                            key={i}
                            style={{ zIndex, filter: `blur(${blur}px)` }}
                            animate={{ x, z, rotateY: rot, scale: sc, opacity: o }}
                            initial={false}
                            transition={{ type: 'spring', stiffness: 100, damping: 18 }}
                            drag="x"
                            dragConstraints={{ left: 0, right: 0 }}
                            onDragEnd={(_, info) => {
                                if (info.offset.x > 80) prev();
                                else if (info.offset.x < -80) next();
                            }}
                        >
                            {child}
                        </Slot>
                    );
                })}
            </Stage>

            <Nav>
                <Arrow aria-label="Previous plan" onClick={prev}>‹</Arrow>
                <Dots>
                    {items.map((_, i) => (
                        <Dot key={i} $active={i === idx} onClick={() => { setIdx(i); onChange?.(i); }} aria-label={`Go to slide ${i + 1}`} />
                    ))}
                </Dots>
                <Arrow aria-label="Next plan" onClick={next}>›</Arrow>
            </Nav>
        </Shell>
    );
}
